import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { convertToXML } from '../utils/api';
import { Form, Select, Input, Button, Upload, Card, Spin, Alert, Row, Col, Typography, message } from 'antd';
import { UploadOutlined, CopyOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
// import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// import { solarizedlight } from 'react-syntax-highlighter/dist/esm/styles/prism';


const { Option } = Select;
const { TextArea } = Input;
const { Title, Paragraph } = Typography;

const DATABASE_VENDORS = [
    { value: 'access', label: 'Microsoft Access' },
    { value: 'ansi', label: 'ANSI SQL' },
    { value: 'athena', label: 'AWS Athena' },
    { value: 'azuresql', label: 'Azure SQL' },
    { value: 'bigquery', label: 'Google BigQuery' },
    { value: 'couchbase', label: 'Couchbase' },
    { value: 'databricks', label: 'Databricks' },
    { value: 'dax', label: 'AWS DAX' },
    { value: 'db2', label: 'IBM DB2' },
    { value: 'exasol', label: 'Exasol' },
    { value: 'firebird', label: 'Firebird' },
    { value: 'gaussdb', label: 'GaussDB' },
    { value: 'generic', label: 'Generic SQL' },
    { value: 'greenplum', label: 'Greenplum' },
    { value: 'hana', label: 'SAP HANA' },
    { value: 'hive', label: 'Apache Hive' },
    { value: 'impala', label: 'Apache Impala' },
    { value: 'informix', label: 'IBM Informix' },
    { value: 'mdx', label: 'MDX' },
    { value: 'mssql', label: 'Microsoft SQL Server' },
    { value: 'mysql', label: 'MySQL' },
    { value: 'netezza', label: 'IBM Netezza' },
    { value: 'odbc', label: 'ODBC' },
    { value: 'openedge', label: 'Progress OpenEdge' },
    { value: 'oracle', label: 'Oracle' },
    { value: 'postgresql', label: 'PostgreSQL' },
    { value: 'presto', label: 'Presto' },
    { value: 'redshift', label: 'Amazon Redshift' },
    { value: 'snowflake', 'label': 'Snowflake' },
    { value: 'soql', label: 'Salesforce SOQL' },
    { value: 'spark', label: 'SparkSQL' },
    { value: 'splex', label: 'SQL-PL Extended' },
    { value: 'sybase', label: 'Sybase' },
    { value: 'teradata', label: 'Teradata' },
    { value: 'trino', label: 'Trino' },
    { value: 'vertica', label: 'Vertica' },
];

const defaultSql = `SELECT   ProductNumber, Category =
      CASE ProductLine
         WHEN 'R' THEN 'Road'
         WHEN 'M' THEN 'Mountain'
         WHEN 'T' THEN 'Touring'
         WHEN 'S' THEN 'Other sale items'
         ELSE 'Not for sale'
      END,
   Name
FROM Production.Product
ORDER BY ProductNumber;`;

interface XMLData {
    usedTime: string;
    'gsp.version': string;
    xml: string;
    dbVendor: string;
}

interface ApiResponse {
    code: number;
    data?: XMLData;
    error?: string;
}

const ToXMLPage: React.FC = () => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [analysisDuration, setAnalysisDuration] = useState<number | null>(null);

    const mutation = useMutation<ApiResponse, Error, FormData>({
        mutationFn: convertToXML,
    });

    const handleCopyToClipboard = () => {
        if (mutation.status === 'success' && mutation.data?.data?.xml) {
            navigator.clipboard.writeText(mutation.data.data.xml)
                .then(() => {
                    message.success('XML content copied to clipboard!');
                })
                .catch(err => {
                    message.error('Failed to copy to clipboard.');
                    console.error('Failed to copy text: ', err);
                });
        }
    };

    const onFinish = async (values: { dbVendor: string; sqlText: string; }) => {
        setAnalysisDuration(null);
        const formData = new FormData();
        formData.append('db_vendor', values.dbVendor);
        
        if (fileList.length > 0 && fileList[0].originFileObj) {
            formData.append('sql_file', fileList[0].originFileObj);
        } else {
            formData.append('sql_text', values.sqlText);
        }
        
        const startTime = performance.now();
        await mutation.mutateAsync(formData);
        const endTime = performance.now();
        setAnalysisDuration(endTime - startTime);
    };

    const handleFileChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
        setFileList(newFileList);
        if (newFileList.length > 0) {
            message.info('File selected. The SQL text input is now disabled.');
        }
    };

    const handleSqlTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (e.target.value) {
            setFileList([]);
        }
    };

    return (
        <>
            <Title level={2}>SQL to XML</Title>
            <Paragraph>
                Convert your SQL scripts to XML format. You can either paste your SQL code directly or upload an SQL file.
            </Paragraph>
            <Row gutter={24}>
                <Col xs={24} md={12}>
                    <Card title="Input your SQL here">
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                            initialValues={{ dbVendor: 'mssql', sqlText: defaultSql }}
                        >
                            <Form.Item
                                name="dbVendor"
                                label="Database Vendor"
                                rules={[{ required: true, message: 'Please select a database vendor!' }]}
                            >
                                <Select showSearch placeholder="Select a vendor">
                                    {DATABASE_VENDORS.map(vendor => (
                                        <Option key={vendor.value} value={vendor.value}>{vendor.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="sqlText"
                                label="SQL Text"
                                rules={[{ required: fileList.length === 0, message: 'Please input SQL text or upload a file!' }]}
                            >
                                <TextArea
                                    rows={10}
                                    placeholder="Enter your SQL script here"
                                    disabled={fileList.length > 0}
                                    onChange={handleSqlTextChange}
                                />
                            </Form.Item>

                            <Form.Item label="Or Upload SQL File">
                                <Upload
                                    name="sqlFile"
                                    beforeUpload={() => false}
                                    onChange={handleFileChange}
                                    fileList={fileList}
                                    maxCount={1}
                                >
                                    <Button icon={<UploadOutlined />} disabled={form.getFieldValue('sqlText')}>
                                        Select File
                                    </Button>
                                </Upload>
                            </Form.Item>

                            <Form.Item>
                                <Button type="primary" htmlType="submit" loading={mutation.status === 'pending'}>
                                    Convert to XML
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card 
                        title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <span style={{ marginRight: '8px' }}>XML Output</span>
                                    {analysisDuration !== null && (
                                        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                                            (Converted in {analysisDuration.toFixed(2)} ms)
                                        </Typography.Text>
                                    )}
                                </div>
                            </div>
                        }
                        extra={
                            mutation.status === 'success' && mutation.data?.data?.xml && (
                                <Button
                                    icon={<CopyOutlined />}
                                    onClick={handleCopyToClipboard}
                                >
                                    Copy
                                </Button>
                            )
                        }
                    >
                        {mutation.status === 'pending' && <Spin tip="Converting..." />}
                        {mutation.status === 'error' && (
                            <Alert message="Error" description={mutation.error.message} type="error" showIcon />
                        )}
                        {mutation.status === 'success' && mutation.data?.data?.xml && (
                            <pre style={{ whiteSpace: 'pre-wrap', background: '#f5f5f5', border: '1px solid #ccc', padding: '10px', borderRadius: '4px' }}>
                                {mutation.data.data.xml}
                            </pre>
                        )}
                         {mutation.status === 'idle' && (
                            <Paragraph type="secondary">The XML output will be displayed here.</Paragraph>
                        )}
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default ToXMLPage; 